import React, { useEffect, useMemo, useState } from "react";
import MarkdownViewer from "../MarkdownViewer";
import { normalizeBrandWebsiteUrl } from '../../utils/brandWebsiteUrl';

type BrandCreationPreviewProps = {
  form: any;
  previewEnabled?: boolean;
};

function BrandCreationPreview({ form, previewEnabled = true }: BrandCreationPreviewProps) {
  const displayDescription = form.description?.trim() ?? '';

  const name = form.displayName ?? form.name ?? 'Brand';
  const tagline = form.tagline;

  const [imgError, setImgError] = useState(false);

  const imageUrl = useMemo(() => {
    const candidate = form?.brandLogoUrl || form?.mediaPreviewUrl || form?.mediaUrl || '';
    return String(candidate || '').trim();
  }, [form?.brandLogoUrl, form?.mediaPreviewUrl, form?.mediaUrl]);

  useEffect(() => {
    setImgError(false);
  }, [imageUrl]);

  const hasImage = Boolean(imageUrl) && !imgError;
  const websiteUrl = normalizeBrandWebsiteUrl(form.websiteUrl || '');
  const purchaseUrl = normalizeBrandWebsiteUrl(form.purchaseUrl || '');

  return (
    <div className="brand-preview-container" data-testid="brand-creation-preview">
      {previewEnabled ? (
        <div className="brand-preview-header" data-testid="brand-creation-preview-header">
          <h2>Live Brand Preview</h2>
        </div>
      ) : null}

      {/* Image Carousel */}
      <div className="brand-preview-carousel">
        <div className="live-preview-media" data-testid="brand-creation-preview-logo" aria-label="Brand logo preview">
          <div className={`live-preview-media-hero${hasImage ? ' live-preview-media-hero--filled' : ' live-preview-media-hero--empty'}`}>
            {hasImage ? (
              <img
                data-testid="brand-creation-preview-logo-image"
                src={imageUrl}
                alt={`${name} logo`}
                onError={() => setImgError(true)}
              />
            ) : (
              <div className="live-preview-media-empty" data-testid="brand-creation-preview-logo-empty" aria-label="No brand logo">
                <div className="carousel-image-placeholder" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* About Brand Section */}
      <div className="brand-preview-about" data-testid="brand-creation-preview-about">
        <div className="brand-header">
          <div className="brand-logo-placeholder">
            {/* Logo placeholder */}
          </div>
          <div className="brand-info">
            <h3 data-testid="brand-creation-preview-about-heading">About {name}</h3>
            <p data-testid="brand-creation-preview-tagline">{tagline}</p>
          </div>
        </div>

        <div className="brand-divider"></div>

        <div className="live-preview-description" data-testid="brand-creation-preview-description" data-color-mode="light">
          <MarkdownViewer markdown={displayDescription || '_No description provided._'} />
        </div>
      </div>

      {/* Purchase Section */}
      <div className="brand-preview-purchase" data-testid="brand-creation-preview-purchase">
        <h4 data-testid="brand-creation-preview-purchase-heading">Where To Purchase {name} Products</h4>

        {websiteUrl && (
          <button
            className="brand-website-button"
            data-testid="brand-creation-preview-website"
            onClick={() => window.open(websiteUrl, '_blank', 'noopener,noreferrer')}
          >
            {name} Website
          </button>
        )}

        {purchaseUrl && (
          <button
            className="brand-purchase-button"
            data-testid="brand-creation-preview-purchase-products"
            onClick={() => window.open(purchaseUrl, '_blank', 'noopener,noreferrer')}
          >
            Purchase Products
          </button>
        )}
      </div>
    </div>
  )
};

export default BrandCreationPreview;
